import { useEffect, useMemo, useState } from "react";
import ModalComponent from "components/modal";
import moment from "moment";
import { IconChevronsDown, IconChevronsUp, IconDownload, IconEqual, IconEye, IconPlus } from "@tabler/icons-react";
import { useSharedState } from "state";
import { useParams, useSearchParams } from "react-router-dom";
import { TaskItemProps } from "types/task";
import {
  addCommentToTask,
  deleteTask,
  getCommentsForTask,
  getTaskById,
  getTaskFileLink,
  getTaskFileNames,
  setTaskStatus,
  uploadTaskFiles,
} from "api/task";
import DangerModal from "components/modal/danger";
import WarningModal from "components/modal/warning";
import getUserInitials from "helpers/get-user-initials";
import { CommentProps } from "types/comment";
import { FileDetails, FilesResponse } from "types/file";
import ForbiddenPage from "components/protected-route/forbidden";
import { PreviewFile } from "helpers/preview-file";

export default function TaskDetail() {
  const { getTasksData, notify } = useSharedState();
  const { taskId } = useParams();
  const [searchParams] = useSearchParams();
  const taskType = searchParams.get("type");

  const [isForbidden, setForbidden] = useState<boolean>(false);
  const [modalTab, setModalTab] = useState<"info" | "journal">("info");
  const [task, setTask] = useState<TaskItemProps | undefined>(undefined);
  const [files, setFiles] = useState<FilesResponse | undefined>(undefined);
  const [comments, setComments] = useState<CommentProps[] | undefined>(undefined);
  const [isFileLoading, setFileLoading] = useState<boolean>(false);

  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [isChangingStatus, setChangingStatus] = useState<boolean>(false);

  const [newComment, setNewComment] = useState<string>("");
  const [isLeavingComment, setLeavingComment] = useState<boolean>(false);

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const deleteTaskDanger = "Вы действительно хотите удалить данную задачу?";
  const changeTaskStatusWarning = "Вы действительно хотите изменить статус данной задачи?";

  const openTaskModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  useEffect(() => {
    initTask();
  }, [taskId]);

  const initTask = async () => {
    try {
      await getTaskData();
      await getFilesData();
      await getCommentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      if (error?.response?.status === 403) {
        notify(err, "danger", "Отказано в доступе");
        setForbidden(true);
      }
    }
  };

  const isStatusChangeable = useMemo(() => {
    if (task?.statuses[0] || task?.statuses[1] || task?.statuses[2] || task?.statuses[3] || task?.statuses[4])
      return true;
    return false;
  }, [task]);

  const isFooterVisible = useMemo(() => {
    if ((isStatusChangeable || task?.can_be_deleted) && !isForbidden) return true;
    return false;
  }, [task, isStatusChangeable, isForbidden]);

  const cancelStatusChange = () => {
    setChangingStatus(false);
    setSelectedStatus(0);
  };

  const cancelCommentLeave = () => {
    setLeavingComment(false);
    setNewComment("");
  };

  const changeTab = (tab: "info" | "journal") => {
    setModalTab(tab);
    cancelStatusChange();
    cancelCommentLeave();
  };

  const getTaskData = async () => {
    try {
      const res: TaskItemProps = await getTaskById(Number(taskId));
      setTask(res);
      console.log(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении задачи");
      throw error;
    }
  };

  const getFilesData = async () => {
    try {
      const res: FilesResponse = await getTaskFileNames(Number(taskId));
      setFiles(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файлов");
    }
  };

  const getCommentsData = async () => {
    try {
      const res: CommentProps[] = await getCommentsForTask(Number(taskId));
      setComments(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении комментариев");
    }
  };

  const uploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const formData = new FormData();

      if (e.target.files?.length) {
        Array.from(e.target.files).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      await uploadTaskFiles(Number(taskId), formData);

      notify("Файл добавлен", "success");
      getFilesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const onFileAction = async (file: FileDetails, action: "preview" | "download") => {
    try {
      const res = await getTaskFileLink(Number(taskId), file);
      PreviewFile(res, action);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении ссылки");
    } finally {
      setFileLoading(false);
    }
  };

  const onTaskDelete = async () => {
    openTaskModal();
    setFormLoading(true);

    try {
      await deleteTask(task?.task_request_id || 0);

      setFormSubmitted(true);
      await getTasksData();
      notify("Задача удалена", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении задачи");
    } finally {
      setFormLoading(false);
    }
  };

  const onTaskStatusChange = async () => {
    openTaskModal();
    setFormLoading(true);

    try {
      await setTaskStatus(task?.task_request_id || 0, selectedStatus);

      cancelStatusChange();
      await initTask();
      await getTasksData();
      notify("Статус задачи обновлён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при обновлении статуса задачи");
    } finally {
      setFormLoading(false);
    }
  };

  const onCommentLeave = async () => {
    setFormLoading(true);

    try {
      await addCommentToTask(Number(taskId) || 0, newComment);

      cancelCommentLeave();
      await getCommentsData();
      notify("Комментарий сохранён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении комментария");
    } finally {
      setFormLoading(false);
    }
  };

  const detailsHeader = (
    <ul className="nav nav-tabs card-header-tabs nav-fill" data-bs-toggle="tabs" role="tablist">
      <li className="nav-item" role="presentation">
        <div
          className="nav-link active cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="true"
          role="tab"
          onClick={() => changeTab("info")}
        >
          Детали задачи
        </div>
      </li>
      <li className="nav-item" role="presentation">
        <div
          className="nav-link cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="false"
          role="tab"
          onClick={() => changeTab("journal")}
        >
          Журнал
        </div>
      </li>
    </ul>
  );

  const detailsBody = (
    <div className="row">
      <div className="datagrid gap-3 col-lg-7 mb-3">
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Наименование</div>
          <div className="datagrid-content">{task?.task_name || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Срок</div>
          <div className="datagrid-content">{moment(task?.deadline).format("DD.MM.YYYY") || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Отдел</div>
          <div className="datagrid-content">{task?.department || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Ответственный</div>
          <div className="datagrid-content">{task?.assigned_to_name || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Проект</div>
          <div className="datagrid-content">{task?.project || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Приоритетность</div>
          <div className="datagrid-content">
            {task?.priority === "Средняя" ? (
              <IconEqual className="text-warning" />
            ) : task?.priority === "Высокая" ? (
              <IconChevronsUp className="text-danger" />
            ) : (
              <IconChevronsDown className="text-primary" />
            )}
          </div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Создатель</div>
          <div className="datagrid-content">{task?.creator_name || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Дата создания</div>
          <div className="datagrid-content">{moment(task?.request_date).format("DD.MM.YYYY") || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Описание</div>
          <div className="datagrid-content">{task?.department || "-"}</div>
        </div>
      </div>

      <div className="col-lg-5 mb-3">
        <div className="datagrid-title d-flex align-items-center justify-content-between text-primary">
          Файлы
          {isFooterVisible && (
            <label htmlFor="new_file" className="btn btn-ghost-primary">
              <IconPlus className="icon" /> Добавить
              <input type="file" id="new_file" onChange={uploadFiles} multiple hidden />
            </label>
          )}
        </div>

        {files?.file_details.length ? (
          <div className="list-group">
            {files.file_details.map((file, index) => (
              <div key={index} className="list-group-item">
                <div className="row align-items-center">
                  <div className="col text-truncate">
                    <div className="d-block text-truncate mt-n1" title={file.file_name}>
                      {file.file_name}
                    </div>
                  </div>

                  <div className="col">
                    <div className="d-block text-truncate mt-n1">
                      {moment(file.saved_datetime).add(5, "hours").format("DD.MM.YY HH:mm") || "-"}
                    </div>
                  </div>

                  <div className="col-auto btn-list flex-nowrap">
                    <button
                      type="button"
                      title="Просмотреть"
                      className="btn btn-icon btn-ghost-primary d-flex gap-2"
                      onClick={() => onFileAction(file, "preview")}
                      disabled={isFileLoading}
                    >
                      {isFileLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <IconEye className="icon" />
                      )}
                    </button>
                    <button
                      type="button"
                      title="Скачать"
                      className="btn btn-icon btn-ghost-primary d-flex gap-2"
                      onClick={() => onFileAction(file, "download")}
                      disabled={isFileLoading}
                    >
                      {isFileLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <IconDownload className="icon" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>Нет файлов</div>
        )}
      </div>
    </div>
  );

  const journalBody = (
    <div className="row">
      <div className="col-lg-12">
        <ul className="timeline">
          {comments?.map((comment, index) => (
            <li key={index} className="timeline-event">
              <div className="timeline-event-icon bg-x-lt">{getUserInitials(comment.user_name)}</div>
              <div className="card timeline-event-card">
                <div className="card-body">
                  <div className="text-secondary float-end">
                    ({moment(comment.comment_date).format("DD.MM.YYYY HH:mm")})
                  </div>
                  <h4>{comment.user_name}</h4>
                  <p className="text-secondary">{comment.logs}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const detailsFooter = (
    <>
      <button
        className="btn btn-ghost-danger d-flex gap-2"
        data-bs-toggle="modal"
        data-bs-target="#modal-delete-task"
        disabled={!task?.can_be_deleted || isFormLoading}
      >
        Удалить
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>

      {isStatusChangeable &&
        (isChangingStatus ? (
          <>
            <select
              className="form-select"
              style={{ width: "160px" }}
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(Number(e.target.value))}
              disabled={isFormLoading}
            >
              <option value="0" disabled>
                Выберите статус
              </option>
              {task?.statuses[1] && <option value="1">Взять в работу</option>}
              {task?.statuses[2] && <option value="2">Отправить на утверждение</option>}
              {task?.statuses[3] && <option value="3">Вернуть в работу</option>}
              {task?.statuses[4] && <option value="4">Завершить</option>}
            </select>

            <button
              className="btn btn-ghost-primary d-flex gap-2"
              onClick={cancelStatusChange}
              disabled={isFormLoading}
            >
              Отмена
              {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
            </button>

            <button
              className="btn btn-success d-flex gap-2"
              data-bs-toggle="modal"
              data-bs-target="#modal-change-task-status"
              disabled={!selectedStatus || isFormLoading}
            >
              Сохранить
              {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
            </button>
          </>
        ) : (
          <button
            className="btn btn-success d-flex gap-2"
            onClick={() => setChangingStatus(true)}
            disabled={isFormLoading}
          >
            Сменить статус
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        ))}
    </>
  );

  const journalFooter = (
    <>
      {isLeavingComment ? (
        <>
          <input
            className="form-control"
            style={{ width: 0, flexGrow: 1 }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && newComment && !isFormLoading && onCommentLeave()}
            disabled={isFormLoading}
          />

          <button className="btn btn-ghost-primary d-flex gap-2" onClick={cancelCommentLeave} disabled={isFormLoading}>
            Отмена
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>

          <button
            className="btn btn-success d-flex gap-2"
            onClick={onCommentLeave}
            disabled={!newComment || isFormLoading}
          >
            Сохранить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        </>
      ) : (
        <button
          className="btn btn-success d-flex gap-2"
          onClick={() => setLeavingComment(true)}
          disabled={isFormLoading}
        >
          Добавить комментарий
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}
    </>
  );

  return (
    <>
      <ModalComponent
        header={detailsHeader}
        body={isForbidden ? <ForbiddenPage /> : modalTab === "info" ? detailsBody : journalBody}
        footer={
          isFooterVisible && modalTab === "info"
            ? detailsFooter
            : isFooterVisible && modalTab === "journal"
            ? journalFooter
            : undefined
        }
        alignHeaderToBottom
        disableClose={isFormLoading}
        shouldModalClose={isFormSubmitted}
        shouldModalNavigate={taskType === "other" ? "/tasks?type=other" : "/tasks"}
      />
      <DangerModal
        modal_id="modal-delete-task"
        dangerDescription={deleteTaskDanger}
        cancel={openTaskModal}
        confirm={onTaskDelete}
      />
      <WarningModal
        modal_id="modal-change-task-status"
        warningDescription={changeTaskStatusWarning}
        cancel={openTaskModal}
        confirm={onTaskStatusChange}
      />
    </>
  );
}
